import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { Home, Car, Users, MessagesSquare, User, Bell, Plus, Shield, LogOut, Calendar, PenSquare } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const TABS = [
  { to: "/home", label: "Home", icon: Home },
  { to: "/rides", label: "Rides", icon: Car },
  { to: "/buddies", label: "Buddies", icon: Users },
  { to: "/campus", label: "Campus", icon: MessagesSquare },
  { to: "/profile", label: "Profile", icon: User },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let live = true;
    const load = () => api.get("/notifications/unread-count").then(({ data }) => live && setUnread(data.count)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { live = false; clearInterval(t); };
  }, [location.pathname]);

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <div className="min-h-screen pb-24 md:pb-10">
      {/* Top nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F9FBF9]/80 border-b border-black/5">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/home" data-testid="nav-logo" className="font-display font-black text-2xl text-primary">
            THIRI
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {TABS.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                data-testid={`nav-${label.toLowerCase()}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                  isActive(to) ? "bg-foreground text-white" : "hover:bg-black/5"
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </Link>
            ))}
            <Link to="/events" data-testid="nav-events"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${isActive("/events") ? "bg-foreground text-white" : "hover:bg-black/5"}`}>
              <Calendar className="w-4 h-4" /> Events
            </Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/chat" data-testid="nav-chat" className="p-2 rounded-full hover:bg-black/5 transition-colors duration-200" title="Chat">
              <MessagesSquare className="w-5 h-5" />
            </Link>
            <Link to="/notifications" data-testid="nav-notifications" className="relative p-2 rounded-full hover:bg-black/5 transition-colors duration-200">
              <Bell className="w-5 h-5" />
              {unread > 0 && (
                <span data-testid="unread-badge" className="absolute -top-0.5 -right-0.5 flex">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" />
                  <span className="relative bg-primary text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {unread}
                  </span>
                </span>
              )}
            </Link>
            {(user?.role === "institution_admin" || user?.role === "super_admin") && (
              <Link to={user.role === "super_admin" ? "/super-admin" : "/admin"} data-testid="nav-admin"
                className="p-2 rounded-full hover:bg-black/5 transition-colors duration-200" title="Admin">
                <Shield className="w-5 h-5" />
              </Link>
            )}
            <button data-testid="nav-logout" onClick={async () => { await logout(); navigate("/"); }}
              className="hidden md:block p-2 rounded-full hover:bg-black/5 transition-colors duration-200" title="Logout">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <motion.div key={location.pathname} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: "easeOut" }}>
          <Outlet />
        </motion.div>
      </main>

      {/* FAB */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="fab-btn"
            className="fixed bottom-24 md:bottom-8 right-4 md:right-8 z-50 w-14 h-14 rounded-full bg-primary text-white shadow-[0_6px_0_0_rgba(0,0,0,0.15)] flex items-center justify-center hover:-translate-y-1 transition-transform duration-200">
            <Plus className="w-6 h-6" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-2xl">
          <DropdownMenuItem data-testid="fab-offer-ride" onClick={() => navigate("/rides?tab=offer")}>
            <Car className="w-4 h-4 mr-2" /> Offer Ride
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="fab-create-post" onClick={() => navigate("/campus?create=1")}>
            <PenSquare className="w-4 h-4 mr-2" /> Create Post
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="fab-create-event" onClick={() => navigate("/events?create=1")}>
            <Calendar className="w-4 h-4 mr-2" /> Create Event
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="fab-find-buddies" onClick={() => navigate("/buddies?tab=discover")}>
            <Users className="w-4 h-4 mr-2" /> Find Buddies
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Bottom nav mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl bg-white/80 border-t border-black/5">
        <div className="flex justify-around py-2">
          {TABS.map(({ to, label, icon: Icon }) => (
            <Link key={to} to={to} data-testid={`bottomnav-${label.toLowerCase()}`}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-2xl text-[10px] font-bold transition-colors duration-200 ${
                isActive(to) ? "bg-foreground text-white" : "text-muted-foreground"
              }`}>
              <Icon className="w-5 h-5" /> {label}
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
}
